import { supabase } from "@/lib/supabase";
import { fetchManufacturingProducts } from "@/lib/api/manufacturing";
import { fetchSeasonDatePresets } from "@/lib/seasonDatePresets";
import { fetchTags } from "@/lib/api/products";

// Lista de productos de fabricacion candidatos al traspaso, marcando si ya
// existen en el catalogo comercial (por sales_product_id o por code).
export async function fetchTransferCandidates(filters = {}) {
  const result = await fetchManufacturingProducts({
    searchTerm: filters.searchTerm || "",
    status: filters.status || "all",
    temporada: filters.temporada || "",
    codFam: filters.codFam || "",
    limit: filters.limit || 500,
  });

  if (result.error) return result;

  const products = result.data || [];
  if (products.length === 0) return { data: [], error: null };

  const linkedIds = products.map((p) => p.sales_product_id).filter(Boolean);
  const codes = products.map((p) => p.code).filter(Boolean);

  const existingByLink = new Set();
  const existingByCode = new Set();

  try {
    if (linkedIds.length > 0) {
      const { data, error } = await supabase.from("products").select("id").in("id", linkedIds);
      if (error) throw error;
      (data || []).forEach((row) => existingByLink.add(row.id));
    }
    if (codes.length > 0) {
      const { data, error } = await supabase.from("products").select("code").in("code", codes);
      if (error) throw error;
      (data || []).forEach((row) => existingByCode.add(row.code));
    }
  } catch (error) {
    console.error("Transfer candidates lookup error:", error);
    return { data: [], error };
  }

  const data = products.map((product) => {
    const existsInSales =
      (product.sales_product_id && existingByLink.has(product.sales_product_id)) ||
      existingByCode.has(product.code);
    return {
      ...product,
      existsInSales,
      action: existsInSales ? "updated" : "created",
    };
  });

  return { data, error: null };
}

// Construye el informe previo a partir de los productos seleccionados.
export function buildTransferPreview(selectedProducts = [], { seasonName = "", tagName = "" } = {}) {
  const items = selectedProducts.map((product) => {
    const warnings = [];
    if (!Number(product.precio_v)) warnings.push("Precio 0");
    if (!Array.isArray(product.sizes) || product.sizes.length === 0) warnings.push("Sin tallas");
    if (!Array.isArray(product.colors) || product.colors.length === 0) warnings.push("Sin colores");
    return {
      id: product.id,
      code: product.code,
      description: product.description,
      action: product.action || (product.existsInSales ? "updated" : "created"),
      precio_v: Number(product.precio_v || 0),
      seasonName,
      tagName,
      warnings,
    };
  });

  return {
    items,
    total: items.length,
    toCreate: items.filter((item) => item.action === "created").length,
    toUpdate: items.filter((item) => item.action === "updated").length,
    withWarnings: items.filter((item) => item.warnings.length > 0).length,
  };
}

// Ejecuta el traspaso via RPC. seasonName es el nombre de temporada (products.temporada),
// tagId el tag a asignar en destino (o null para no tocar tags).
export async function executeTransfer({ ids = [], seasonName = "", tagId = null } = {}) {
  if (!Array.isArray(ids) || ids.length === 0) {
    return { data: null, error: new Error("No hay productos seleccionados para traspasar") };
  }
  try {
    const { data, error } = await supabase.rpc("transfer_mfg_products_to_sales", {
      p_ids: ids,
      p_temporada: seasonName || null,
      p_tag_id: tagId || null,
    });
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error("Product transfer error:", error);
    return { data: null, error };
  }
}

export { fetchSeasonDatePresets, fetchTags };
